import { Component, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, Validators, FormGroup, FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { FormErrors } from '../../../shared/configs/form-errors.config';
import { CustomError } from '../../../shared/models/api-response';
import { HelperService } from '../../services/helper/helper.service';
import { AuthService } from '../../services/http/auth/auth.service';
import { PushNotificationService } from '../../services/push-notification/push-notification.service';
import { ValidationService } from '../../services/validation/validation.service';

import { RecaptchaComponent } from 'ng-recaptcha';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnDestroy {
  @ViewChild('captchaRef') captchaRef!: RecaptchaComponent;
  @ViewChild('otpCaptchaRef') otpCaptchaRef!: RecaptchaComponent;

  isCaptchaShown: boolean = environment.isCaptchaShown;
  isLoading: boolean = false;
  isLoginWithOtp: boolean = true;
  isVerifyOtp: boolean = false;
  loginForm!: FormGroup;
  otpTokenResponse: string = '';
  resendOtpTimeLeft: number = 0;
  token: string | undefined;

  private destroy$: Subject<void> = new Subject<void>();
  private timerInterval: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private formBuilder: FormBuilder,
    private helperService: HelperService,
    private ngZone: NgZone,
    private pushNotificationService: PushNotificationService,
    private router: Router,
    private validationService: ValidationService,
  ) {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.loginForm = this.formBuilder.group({
      username: [
        null,
        [
          Validators.required,
          Validators.pattern(this.validationService.emailRegex),
          Validators.maxLength(environment.maxCharLimit),
        ],
      ],
      password: [null, [Validators.required, Validators.maxLength(environment.maxCharLimit)]],
    });

    if (this.isCaptchaShown) {
      this.loginForm.addControl(
        'mobile',
        new FormControl(null, [
          Validators.required,
          this.validationService.regexValidator(this.validationService.contactNumberRegex, {
            [FormErrors.registrationMobile]: true,
          }),
        ]),
      );

      this.loginForm.addControl('recaptcha_response', new FormControl(null, Validators.required));

      this.loginForm.addControl(
        'recaptcha_response_otp',
        new FormControl(null, [
          this.validationService.conditionalRequiredValidator(() => this.isVerifyOtp === true),
        ]),
      );

      this.loginForm.addControl(
        'otp',
        new FormControl(null, [
          this.validationService.conditionalRequiredValidator(() => this.isVerifyOtp === true),
          Validators.minLength(4),
        ]),
      );

      this.token = undefined;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCaptchaResolved(token: any): void {
    this.loginForm.patchValue({ recaptcha_response: token });
  }

  onCaptchaResolvedOtp(token: any): void {
    this.loginForm.patchValue({ recaptcha_response_otp: token });
  }

  onClickForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  onClickResendOtp(): void {
    this.startTimer();
    this.fetchOtp();
  }

  onClickVerifyOTP(): void {
    const formData = {
      otp: this.loginForm.get(['otp'])?.value,
      recaptcha_response: this.loginForm.get(['recaptcha_response_otp'])?.value,
      token: this.otpTokenResponse,
    };

    this.authService
      .verifyAdminLoginOtp(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.helperService.showMessage('success', res.message);
          this.getFcmTokenFromFirebase();
          this.navigateToNextPage();
        },
        error: (err: CustomError) => {
          // Reset reCAPTCHA if OTP verification fails
          if (this.otpCaptchaRef) {
            this.otpCaptchaRef.reset();
          }
          // Error handling
          this.helperService.showMessage('error', err.errors.message[0]);
          this.validationService.addResponseErrorToForm(this.loginForm, err);
        },
      });
  }

  onSubmit(): void {
    if (this.isCaptchaShown) {
      this.onSubmitCaptchaLogin();
    } else {
      this.onSubmitLogin();
    }
  }

  onSubmitCaptchaLogin(): void {
    if (this.loginForm.invalid) {
      return;
    }

    this.isLoading = true;
    this.isLoginWithOtp = true;
    this.isVerifyOtp = false;
    const formData = {
      ...this.loginForm.value,
    };

    this.authService
      .captchaLogin(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res.token) {
            this.helperService.showMessage('success', res.message);
            this.isVerifyOtp = true;
            this.isLoginWithOtp = false;
            this.otpTokenResponse = res.token;
          }
        },
        error: (err: CustomError) => {
          // Error
          // Reset reCAPTCHA if login fails
          if (this.captchaRef) {
            this.captchaRef.reset();
          }
          this.isLoading = false;
          this.helperService.showMessage('error', err.errors.message[0]);
          this.validationService.addResponseErrorToForm(this.loginForm, err);
        },
      });
  }

  onSubmitLogin(): void {
    if (this.loginForm.invalid) {
      return;
    }

    const formData = {
      ...this.loginForm.value,
    };
    this.isLoading = true;

    this.authService
      .login(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          this.helperService.showMessage('success', res.message);
          this.getFcmTokenFromFirebase();
          this.navigateToNextPage();
        },
        error: (err: CustomError) => {
          this.isLoading = false;
          this.helperService.showMessage('error', err.message);
        },
      });
  }

  private fetchOtp(): void {
    const formData = {
      mobile: this.loginForm.get('mobile')?.value,
    };
    this.authService
      .fetchAdminLoginOtp(formData, [422])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // Success
          this.helperService.showMessage('success', res.message);
          this.isVerifyOtp = true;
          this.isLoginWithOtp = false;
        },
        error: (err: CustomError) => {
          // Error
          this.isLoading = false;
          this.helperService.showMessage('error', err.errors.message[0]);
          this.validationService.addResponseErrorToForm(this.loginForm, err);
        },
      });
  }

  private getFcmTokenFromFirebase(): void {
    // Get firebase FCM token
    this.pushNotificationService.getFcmTokenFromFirebase().subscribe();
  }

  private navigateToNextPage(): void {
    const { redirectUrl } = this.activatedRoute?.snapshot?.queryParams;
    if (redirectUrl != null) {
      // If redirect url is present
      const urlObj = new URL(window.location.origin + redirectUrl);
      const queryParams: any = {};
      urlObj.searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });
      // Setting additional redirect flag to target url
      queryParams.isRedirect = true;
      this.router.navigate([urlObj.pathname], {
        queryParams,
      });
    } else {
      // If no redirect url is present
      this.router.navigateByUrl('/quest');
    }
  }

  private startTimer(): void {
    // Clear any previous intervals
    this.stopTimer();
    this.resendOtpTimeLeft = 120;
    this.timerInterval = setInterval(() => {
      this.ngZone.run(() => {
        if (this.resendOtpTimeLeft > 0) {
          this.resendOtpTimeLeft -= 1;
        } else {
          this.stopTimer(); // Stop the timer when it reaches 0
        }
      });
    }, 1000); // Update every 1 second
  }

  private stopTimer(): void {
    clearInterval(this.timerInterval);
  }
}
